// src/components/PortalLogin.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import {
    signInWithEmailAndPassword,
    getMultiFactorResolver,
    TotpMultiFactorGenerator,
    PhoneAuthProvider,
    PhoneMultiFactorGenerator,
    RecaptchaVerifier
} from 'firebase/auth';
import { useToast } from '../Toast/ToastContainer';

const PortalLogin = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);

    // MFA States
    const [showMfaScreen, setShowMfaScreen] = useState(false);
    const [mfaCode, setMfaCode] = useState('');
    const [mfaResolver, setMfaResolver] = useState(null);
    const [mfaHints, setMfaHints] = useState([]);
    const [activeHintIndex, setActiveHintIndex] = useState(0);
    const [smsSent, setSmsSent] = useState(false);
    const [verificationId, setVerificationId] = useState('');

    const navigate = useNavigate();
    const { addToast } = useToast();

    // 1. Setup Invisible reCAPTCHA for SMS Login
    const setupRecaptcha = () => {
        if (!window.recaptchaVerifier) {
            window.recaptchaVerifier = new RecaptchaVerifier(auth, 'login-recaptcha', {
                'size': 'invisible'
            });
        }
    };

    // 2. Initial Login Phase
    const handleLogin = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            addToast("Please enter both email and password.", "error");
            return;
        }

        setLoading(true);
        try {
            await signInWithEmailAndPassword(auth, email, password);
            navigate('/portal-dashboard');
        } catch (error) {
            if (error.code === 'auth/multi-factor-auth-required') {
                const resolver = getMultiFactorResolver(auth, error);
                setMfaResolver(resolver);
                setMfaHints(resolver.hints);

                // Prioritize TOTP if available, otherwise default to first (which would be SMS)
                const totpIndex = resolver.hints.findIndex(h => h.factorId === 'totp');
                setActiveHintIndex(totpIndex > -1 ? totpIndex : 0);

                setShowMfaScreen(true);
            } else if (error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found' || error.code === 'auth/invalid-credential') {
                addToast("Invalid email or password.", "error");
            } else {
                console.error("Login error:", error);
                addToast("An error occurred during login. Please try again.", "error");
            }
        } finally {
            setLoading(false);
        }
    };

    // 3. Trigger SMS Dispatch
    const handleSendSms = async () => {
        const hint = mfaHints[activeHintIndex];
        if (!hint || hint.factorId !== 'phone') return;

        setLoading(true);
        try {
            setupRecaptcha();
            const phoneProvider = new PhoneAuthProvider(auth);
            const verId = await phoneProvider.verifyPhoneNumber(
                { multiFactorHint: hint, session: mfaResolver.session },
                window.recaptchaVerifier
            );
            setVerificationId(verId);
            setSmsSent(true);
            addToast("Text message sent!", "success");
        } catch (error) {
            console.error("SMS Dispatch Failed:", error);
            if (window.recaptchaVerifier) {
                window.recaptchaVerifier.clear();
                window.recaptchaVerifier = null;
            }
            addToast("Failed to send text message. Please try again.", "error");
        } finally {
            setLoading(false);
        }
    };

    // 4. MFA Verification Phase (Handles both SMS and Authenticator)
    const handleMfaSubmit = async (e) => {
        e.preventDefault();
        if (mfaCode.length !== 6 || !mfaResolver) {
            addToast("Please enter the 6-digit code.", "error");
            return;
        }

        setLoading(true);
        try {
            const activeHint = mfaHints[activeHintIndex];
            let assertion;

            if (activeHint.factorId === 'totp') {
                assertion = TotpMultiFactorGenerator.assertionForSignIn(activeHint.uid, mfaCode);
            } else if (activeHint.factorId === 'phone') {
                if (!verificationId) throw new Error("Please request a text message first.");

                // FIXED: Create standard credential first, then generate assertion
                const phoneCredential = PhoneAuthProvider.credential(verificationId, mfaCode);
                assertion = PhoneMultiFactorGenerator.assertion(phoneCredential);
            }

            await mfaResolver.resolveSignIn(assertion);
            navigate('/portal-dashboard');

        } catch (error) {
            console.error("MFA Verification Failed:", error);
            addToast("Invalid code. Please try again.", "error");
        } finally {
            setLoading(false);
        }
    };

    const toggleHint = () => {
        setActiveHintIndex((prev) => (prev + 1) % mfaHints.length);
        setSmsSent(false);
        setMfaCode('');
    };

    const activeHint = mfaHints[activeHintIndex];

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8 font-sans">
            {/* The Invisible reCAPTCHA container required for SMS login */}
            <div id="login-recaptcha"></div>

            <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
                <div className="mx-auto h-16 w-16 bg-blue-600 rounded-2xl shadow-lg flex items-center justify-center mb-4">
                    <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                </div>
                <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white">
                    {showMfaScreen ? 'Two-Step Verification' : 'Client Portal'}
                </h2>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    {showMfaScreen
                        ? (activeHint?.factorId === 'totp' ? 'Enter the code generated by your Authenticator app.' : `Secure text message verification.`)
                        : 'Sign in to securely manage your tax files.'}
                </p>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow-xl sm:rounded-2xl sm:px-10 border border-gray-100 dark:border-gray-700">

                    {/* STANDARD LOGIN FORM */}
                    {!showMfaScreen ? (
                        <form className="space-y-6" onSubmit={handleLogin}>
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email address</label>
                                <div className="mt-1">
                                    <input id="email" name="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                                        className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">Password</label>
                                <div className="mt-1">
                                    <input id="password" name="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                                        className="appearance-none block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                </div>
                            </div>

                            <button type="submit" disabled={loading}
                                className={`w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                {loading ? 'Signing in...' : 'Sign in'}
                            </button>
                        </form>
                    ) : (

                        /* MFA VERIFICATION FORMS */
                        <div className="space-y-6">

                            {activeHint?.factorId === 'phone' && !smsSent ? (
                                // SMS Launch Pad
                                <div className="text-center">
                                    <div className="bg-gray-50 dark:bg-gray-700/50 p-6 rounded-xl border border-gray-200 dark:border-gray-600 mb-6">
                                        <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Send a text message to:</p>
                                        <p className="text-xl font-bold text-gray-900 dark:text-white tracking-widest">{activeHint.phoneNumber}</p>
                                    </div>
                                    <button onClick={handleSendSms} disabled={loading} className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 transition-all ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                        {loading ? 'Sending...' : 'Send Verification Code'}
                                    </button>
                                </div>
                            ) : (
                                // Code Entry Form (Used for both TOTP and SMS once sent)
                                <form onSubmit={handleMfaSubmit} className="space-y-6">
                                    {activeHint?.factorId === 'phone' && (
                                        <p className="text-center text-sm font-medium text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 p-2 rounded-lg">
                                            Code sent to {activeHint.phoneNumber}
                                        </p>
                                    )}
                                    <div>
                                        <label htmlFor="mfaCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-center mb-4">
                                            6-Digit Code
                                        </label>
                                        <input
                                            id="mfaCode" name="mfaCode" type="text" inputMode="numeric" pattern="[0-9]*" maxLength="6" required autoFocus
                                            value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                                            className="appearance-none block w-full px-4 py-3 text-center text-2xl tracking-widest border border-gray-300 dark:border-gray-600 rounded-xl shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
                                            placeholder="000000"
                                        />
                                    </div>

                                    <button type="submit" disabled={loading || mfaCode.length !== 6}
                                        className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${loading || mfaCode.length !== 6 ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        {loading ? 'Verifying...' : 'Verify Code'}
                                    </button>
                                </form>
                            )}

                            {/* Options & Cancels */}
                            <div className="flex flex-col gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                                {mfaHints.length > 1 && (
                                    <button type="button" onClick={toggleHint} className="w-full text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                                        Use a different method ({activeHint?.factorId === 'totp' ? 'Text Message' : 'Authenticator App'})
                                    </button>
                                )}
                                <button type="button" onClick={() => { setShowMfaScreen(false); setMfaCode(''); setSmsSent(false); }} className="w-full text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300">
                                    Cancel Login
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PortalLogin;